/** @jsxFrag React.Fragment */

/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage, FormattedNumber } from 'js/lib/coursera.react-intl';

import { Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import GrowthDiscoveryExperiments from 'bundles/epic/clients/GrowthDiscovery';
import { RegionalPromotionFullWidthBanner } from 'bundles/front-page/components/banners/regional-promotion-banner/RegionalPromotionFullWidthBanner';
import ReactPriceDisplay from 'bundles/payments-common/components/ReactPriceDisplay';

import _t from 'i18n!nls/front-page';

const styles = {
  link: css`
    text-decoration: underline;
  `,
};

type Props = {
  variant?: 'blue' | 'purple' | 'light-blue';
};

const RegionalPromotionBanner = ({ variant }: Props) => {
  const track = useTracker();

  const onClick = (url: string) => () => {
    track('click_button', {
      button: {
        name: 'promo_cta',
        linkType: 'internal',
        linkURL: url,
      },
      pageSection: {
        sectionName: 'promo_bar',
      },
    });
  };

  const getBannerContent = () => {
    if (GrowthDiscoveryExperiments.get('regional2025JunePromoGlobal')) {
      const link = '/courseraplus/special/summer-2025-global-200-off';
      return (
        <Typography2 component="p" variant="subtitleMedium">
          <FormattedMessage
            message={_t(
              `Early bird sale! Unlock {numberOfWays}+ courses from Google, IBM, and more for 50% off. {claim}`
            )}
            numberOfWays={<FormattedNumber value={10000} />}
            claim={
              <a css={styles.link} href={link} onClick={onClick(link)}>
                {_t('Save today')}
              </a>
            }
          />
        </Typography2>
      );
    }
    if (GrowthDiscoveryExperiments.get('regional2025JunePromoGermany')) {
      const link = '/courseraplus/special/germany-early-bird-190-off';
      return (
        <Typography2 component="p" variant="subtitleMedium">
          <FormattedMessage
            message={_t(
              `Flash sale! {numberOfWays}+ courses from Google, IBM, and more for {price}/year. Now with AI dubbing. {claim}`
            )}
            numberOfWays={<FormattedNumber value={10000} />}
            price={<ReactPriceDisplay value={185} currency="EUR" hideCurrencyCode />}
            claim={
              <a css={styles.link} href={link} onClick={onClick(link)}>
                {_t('Save now')}
              </a>
            }
          />
        </Typography2>
      );
    }

    if (GrowthDiscoveryExperiments.get('regional2025JunePromoUK')) {
      const link = '/courseraplus/special/uk-early-bird-150';
      return (
        <Typography2 component="p" variant="subtitleMedium">
          <FormattedMessage
            message={_t(
              `Early bird sale! Unlock {numberOfWays}+ courses from Google, Microsoft, and more for {price}/year. {claim}`
            )}
            numberOfWays={<FormattedNumber value={10000} />}
            price={<ReactPriceDisplay value={160} currency="GBP" hideCurrencyCode />}
            claim={
              <a css={styles.link} href={link} onClick={onClick(link)}>
                {_t('Save now')}
              </a>
            }
          />
        </Typography2>
      );
    }

    if (GrowthDiscoveryExperiments.get('regional2025JunePromoIndia')) {
      const link = '/courseraplus/special/india-summer-promotion';

      return (
        <Typography2 component="p" variant="subtitleMedium">
          <FormattedMessage
            message={_t(
              `Early bird sale! Unlock {numberOfWays}+ courses from Google, IBM, and more for {promoPrice}/year. {claim}`
            )}
            numberOfWays={<FormattedNumber value={10000} />}
            promoPrice={<ReactPriceDisplay value={7999} currency="INR" hideCurrencyCode />}
            claim={
              <a css={styles.link} href={link} onClick={onClick(link)}>
                {_t('Save now')}
              </a>
            }
          />
        </Typography2>
      );
    }

    if (GrowthDiscoveryExperiments.get('regional2025JunePromoEmergingMarkets')) {
      const link = '/courseraplus/special/em-summer-promotion';
      return (
        <Typography2 component="p" variant="subtitleMedium">
          <FormattedMessage
            message={_t(
              `Heat up your career this summer with 65% off top courses from Google, Microsoft, and more. {claim}`
            )}
            claim={
              <a css={styles.link} href={link} onClick={onClick(link)}>
                {_t('Save now')}
              </a>
            }
          />
        </Typography2>
      );
    }

    if (GrowthDiscoveryExperiments.get('regional2025JunePromoLatam')) {
      const link = '/courseraplus/special/latam-june-promotion';
      return (
        <Typography2 component="p" variant="subtitleMedium">
          <FormattedMessage
            message={_t(`Get 65% off courses from Google, Microsoft, and more. Now available with AI dubbing. {claim}`)}
            claim={
              <a css={styles.link} href={link} onClick={onClick(link)}>
                {_t('Save now')}
              </a>
            }
          />
        </Typography2>
      );
    }
    return null;
  };

  const bannerContent = getBannerContent();

  if (!bannerContent) {
    return null;
  }

  // This div wrapper is required.
  // This wrapper prevents some sort of hydration related bug which leaks into the hero section below.
  return (
    <div>
      <RegionalPromotionFullWidthBanner variant={variant}>{getBannerContent()}</RegionalPromotionFullWidthBanner>
    </div>
  );
};

export { RegionalPromotionBanner };

export default RegionalPromotionBanner;
